import { CSSProperties, useId, useMemo, useState } from "react";
import { classNames } from "primereact/utils";
import styles from "./DesignStudioDialog.module.scss";
import { AppDialog } from "../../../../../common/components/AppDialog/AppDialog";
import { DefaultButton } from "../../../../../common/components/button/defaultButton/DefaultButton";
import {
  designStudioFamilies,
  designStudioPresets,
  IDesignStudioColors,
} from "./models/designStudioPresetModel";
import { DesignStudioMode, useDesignStudio } from "./hooks/useDesignStudio";
import {
  buildDesignStudioAccentScope,
  buildDesignStudioPreviewStyle,
} from "./utils/designStudioPreviewUtility";
import { DesignStudioPresetCard } from "./components/DesignStudioPresetCard";
import { DesignStudioAdvancedEditor } from "./components/DesignStudioAdvancedEditor";
import { DesignStudioMock } from "./components/DesignStudioMock";
import { DesignStudioEmailPreview } from "./components/DesignStudioEmailPreview";
import { DesignStudioConfirmationPreview } from "./components/DesignStudioConfirmationPreview";
import { DesignStudioSwatches } from "./components/DesignStudioSwatches";

interface IDesignStudioDialogProps {
  visible: boolean;
  initialColors: IDesignStudioColors;
  /** Where each opening lands: the gallery, or the hand editor (e.g. to fix a bad colour). */
  initialMode?: DesignStudioMode;
  studioName?: string;
  onApply: (colors: IDesignStudioColors) => void;
  onClose: () => void;
}

const MODES: { value: DesignStudioMode; label: string }[] = [
  { value: "presets", label: "Палитри" },
  { value: "advanced", label: "Разширени" },
];

type PreviewTab = "app" | "email" | "confirmation";

const PREVIEW_TABS: { value: PreviewTab; label: string }[] = [
  { value: "app", label: "Приложение" },
  { value: "email", label: "Имейл" },
  { value: "confirmation", label: "Потвърждение" },
];

const presetCount = (familyId: string) =>
  designStudioPresets.filter((preset) => preset.family === familyId).length;

export const DesignStudioDialog = ({
  visible,
  initialColors,
  initialMode = "presets",
  studioName,
  onApply,
  onClose,
}: IDesignStudioDialogProps) => {
  const titleId = useId();
  const studio = useDesignStudio(initialColors, initialMode);
  const { colors, mode, familyFilter, matchedPreset, paletteName, hasOwnAccent } = studio;
  // Narrow screens show one preview at a time; wide ones always show all three.
  const [previewTab, setPreviewTab] = useState<PreviewTab>("app");

  // Every opening starts from the studio's current colours; picks made and cancelled
  // last time are gone. Reset while rendering, so the old picks never flash on screen.
  // Only an opening resets: initialColors is a new object on every render of the parent,
  // so following it would wipe the picks while the dialog is open.
  const [wasVisible, setWasVisible] = useState(visible);
  if (visible !== wasVisible) {
    setWasVisible(visible);
    if (visible) {
      studio.reset(initialColors, initialMode);
      setPreviewTab("app");
    }
  }

  const scopeStyle = useMemo(
    () =>
      ({
        ...buildDesignStudioPreviewStyle(colors),
        ...buildDesignStudioAccentScope(colors),
      }) as CSSProperties,
    [colors],
  );

  const visibleFamilies = useMemo(
    () =>
      designStudioFamilies.filter(
        (family) => familyFilter === "all" || family.id === familyFilter,
      ),
    [familyFilter],
  );

  return (
    <AppDialog
      visible={visible}
      onHide={onClose}
      showHeader={false}
      dismissableMask={false}
      ariaLabelledBy={titleId}
      className={styles.dialog}
      maskClassName={styles.mask}
    >
      <div className={styles.root} style={scopeStyle} data-mode={mode}>
        <header className={styles.header}>
          <div className={styles.header_text}>
            <h2 id={titleId} className={styles.title}>
              Design Studio
            </h2>
            <p className={styles.subtitle}>
              Изберете готова палитра или създайте своя.
            </p>
          </div>

          <div className={styles.header_side}>
            <div
              className={styles.segmented}
              role="group"
              aria-label="Начин на работа"
              data-active={mode}
            >
              <span className={styles.segmented_thumb} aria-hidden />
              {MODES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={classNames(styles.segmented_option, {
                    [styles.segmented_option_active]: mode === option.value,
                  })}
                  aria-pressed={mode === option.value}
                  onClick={() => studio.setMode(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <button
              type="button"
              className={styles.close}
              aria-label="Затвори"
              onClick={onClose}
            >
              <svg viewBox="0 0 16 16" aria-hidden>
                <path
                  d="M4 4l8 8M12 4l-8 8"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </header>

        <div className={styles.body}>
          <aside className={styles.preview} aria-label="Преглед на живо" data-tab={previewTab}>
            <div className={styles.preview_label}>
              <span className={styles.preview_caption}>Преглед</span>
              <span className={styles.preview_name} data-testid="design-studio-palette-name">
                {paletteName}
              </span>
              <DesignStudioSwatches colors={colors} />
            </div>

            <div className={styles.preview_tabs} role="group" aria-label="Изглед на прегледа">
              {PREVIEW_TABS.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  className={classNames(styles.tab, {
                    [styles.tab_active]: previewTab === tab.value,
                  })}
                  aria-pressed={previewTab === tab.value}
                  onClick={() => setPreviewTab(tab.value)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className={styles.preview_stage}>
              <DesignStudioMock
                colors={colors}
                variant="full"
                studioName={studioName}
                className={styles.preview_app}
              />
              <DesignStudioEmailPreview
                colors={colors}
                studioName={studioName}
                className={styles.preview_email}
              />
              <DesignStudioConfirmationPreview
                colors={colors}
                studioName={studioName}
                className={styles.preview_confirmation}
              />
            </div>

            {!hasOwnAccent && (
              <p className={styles.preview_note} role="note">
                Без собствен акцент: приложението ползва вградения лилав акцент, а порталът
                оцветява избора с фоновите цветове.
              </p>
            )}
          </aside>

          <div className={styles.content}>
            {mode === "presets" ? (
              <div className={styles.gallery}>
                <p className={styles.current}>
                  <span className={styles.current_label}>Текуща:</span>
                  <span className={styles.current_name}>{paletteName}</span>
                </p>

                <div className={styles.filters} role="group" aria-label="Стил на палитрата">
                  <button
                    type="button"
                    className={classNames(styles.filter, {
                      [styles.filter_active]: familyFilter === "all",
                    })}
                    aria-pressed={familyFilter === "all"}
                    onClick={() => studio.setFamilyFilter("all")}
                  >
                    Всички
                    <span className={styles.filter_count}>{designStudioPresets.length}</span>
                  </button>
                  {designStudioFamilies.map((family) => (
                    <button
                      key={family.id}
                      type="button"
                      className={classNames(styles.filter, {
                        [styles.filter_active]: familyFilter === family.id,
                      })}
                      aria-pressed={familyFilter === family.id}
                      onClick={() => studio.setFamilyFilter(family.id)}
                    >
                      {family.name}
                      <span className={styles.filter_count}>{presetCount(family.id)}</span>
                    </button>
                  ))}
                </div>

                {visibleFamilies.map((family) => (
                  <section
                    key={family.id}
                    className={styles.family}
                    aria-labelledby={`${titleId}-${family.id}`}
                  >
                    <header className={styles.family_header}>
                      <h3 id={`${titleId}-${family.id}`} className={styles.family_name}>
                        {family.name}
                      </h3>
                    </header>

                    <div className={styles.grid}>
                      {designStudioPresets
                        .filter((preset) => preset.family === family.id)
                        .map((preset) => (
                          <DesignStudioPresetCard
                            key={preset.key}
                            preset={preset}
                            selected={matchedPreset?.key === preset.key}
                            onSelect={studio.selectPreset}
                          />
                        ))}
                    </div>
                  </section>
                ))}
              </div>
            ) : (
              <DesignStudioAdvancedEditor studio={studio} />
            )}
          </div>
        </div>

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.defaults}
            onClick={studio.resetToDefaults}
          >
            <svg viewBox="0 0 16 16" aria-hidden>
              <path
                d="M3.2 6.2A5 5 0 1 1 3 9.5M3 3v3.4h3.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            По подразбиране
          </button>

          <div className={styles.footer_actions}>
            <span className={styles.footer_button}>
              <DefaultButton label="Откажи" design="outline" width="100%" onClick={onClose} />
            </span>
            <span className={styles.footer_button}>
              <DefaultButton
                label="Приложи"
                design="accent"
                width="100%"
                onClick={() => onApply({ ...colors })}
              />
            </span>
          </div>
        </footer>
      </div>
    </AppDialog>
  );
};
